import type { ReactNode } from "react";
import { useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check } from "lucide-react";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { deriveAnalyticsElementId } from "@/lib/analytics/deriveAnalyticsElementId";
import { resolveFormAnalytics } from "@/lib/analytics/resolveFormAnalytics";
import { createLogger } from "@/lib/logging/logger";
import { siteConfig } from "@/lib/seo/metadata";
import type { RouterInputs } from "@/lib/trpc";
import { useTRPC } from "@/lib/trpc";
import { Button } from "@/shared/ui/button";
import { ExternalLink } from "@/shared/ui/external-link";

const logger = createLogger("DemoRequestForm");

type DemoRequest = RouterInputs["marketing"]["submitDemoRequest"];
type TeamType = NonNullable<DemoRequest["teamType"]>;
type Service = NonNullable<DemoRequest["service"]>;

const TEAM_TYPES: readonly { value: TeamType; label: string }[] = [
  { value: "agency", label: "Agency" },
  { value: "brand", label: "Brand" },
  { value: "other", label: "Other" },
];

const SERVICES: readonly { value: Service; label: string }[] = [
  { value: "antiBotting", label: "Anti-botting" },
  { value: "brandSafety", label: "Brand Safety" },
  { value: "measuring", label: "Measuring" },
];

// Nothing is preselected: a default would report a team type and an interest for every visitor
// who never touched the control, which is worse than not asking. `""` is "not answered".
const EMPTY_FIELDS: {
  name: string;
  email: string;
  company: string;
  teamType: TeamType | "";
  service: Service | "";
  message: string;
} = { name: "", email: "", company: "", teamType: "", service: "", message: "" };

const Field = ({ id, label, children }: { id: string; label: string; children: ReactNode }) => (
  <div className="dl-demo-form__field">
    <label htmlFor={id}>{label}</label>
    {children}
  </div>
);

type ChoiceFieldProps<T extends string> = {
  id: string;
  name: string;
  label: string;
  options: readonly { value: T; label: string }[];
  value: T | "";
  onChange: (value: T) => void;
  /**
   * The group owns a whole row rather than sharing one. It has room to confirm the choice with a
   * mark as well as the fill, and its longer labels stack on a phone instead of staying three-up.
   */
  wide?: boolean;
};

/* A labelled radiogroup rather than a fieldset: a `<legend>` is laid out outside its fieldset's
   flow, which loses the 6px that puts it above the options like every other field label. */
const ChoiceField = <T extends string>({ id, name, label, options, value, onChange, wide }: ChoiceFieldProps<T>) => (
  <div className="dl-demo-form__field">
    <p id={id}>{label}</p>
    <div
      className={`dl-demo-form__options${wide ? " dl-demo-form__options--wide" : ""}`}
      role="radiogroup"
      aria-labelledby={id}
    >
      {options.map((option) => (
        <label className="dl-demo-form__option" key={option.value}>
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
          {wide && value === option.value && <Check size={20} aria-hidden="true" />}
        </label>
      ))}
    </div>
  </div>
);

type DemoRequestFormProps = {
  baseAnalyticsElementId: AnalyticsElementId;
};

export const DemoRequestForm = ({ baseAnalyticsElementId }: DemoRequestFormProps) => {
  const fieldId = useId();
  const trpc = useTRPC();
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [submitted, setSubmitted] = useState(false);

  const { mutate, isPending, isError } = useMutation(
    trpc.marketing.submitDemoRequest.mutationOptions({
      onSuccess: () => {
        setSubmitted(true);
        setFields(EMPTY_FIELDS);
      },
      onError: (error) => {
        // The visitor already sees the inline failure; this is for diagnosing a broken form.
        logger.error("Demo request submission failed", { attributes: { code: error.data?.code } });
      },
    }),
  );

  if (submitted) {
    return (
      <p className="dl-demo-form__done" role="status">
        Thanks — we have your enquiry.
      </p>
    );
  }

  const update = (field: "name" | "email" | "company" | "message") => (event: { target: { value: string } }) =>
    setFields((current) => ({ ...current, [field]: event.target.value }));
  const formAnalytics = resolveFormAnalytics({
    analyticsElementId: deriveAnalyticsElementId(baseAnalyticsElementId, "submit"),
    disabled: isPending,
    onSubmit: (event) => {
      event.preventDefault();
      // Only the name and email are required, so every other field is left out entirely rather
      // than sent as a blank the API would record as an answer.
      const company = fields.company.trim();
      const message = fields.message.trim();
      mutate({
        name: fields.name.trim(),
        email: fields.email.trim(),
        ...(company ? { company } : {}),
        ...(fields.teamType ? { teamType: fields.teamType } : {}),
        ...(fields.service ? { service: fields.service } : {}),
        ...(message ? { message } : {}),
      });
    },
  });

  return (
    <form className="dl-demo-form" noValidate={false} onSubmit={formAnalytics.onSubmit}>
      <div className="dl-demo-form__row">
        <Field id={`${fieldId}-name`} label="Full name">
          <input
            id={`${fieldId}-name`}
            name="name"
            autoComplete="name"
            placeholder="Jane Doe"
            maxLength={120}
            pattern=".*\S.*"
            title="Enter your name."
            required
            value={fields.name}
            onChange={update("name")}
          />
        </Field>
        <Field id={`${fieldId}-email`} label="Work email">
          <input
            id={`${fieldId}-email`}
            name="email"
            type="email"
            autoComplete="email"
            placeholder="jane@company.com"
            maxLength={254}
            required
            value={fields.email}
            onChange={update("email")}
          />
        </Field>
      </div>
      <div className="dl-demo-form__row">
        <Field id={`${fieldId}-company`} label="Company">
          <input
            id={`${fieldId}-company`}
            name="company"
            autoComplete="organization"
            placeholder="Company name"
            maxLength={160}
            value={fields.company}
            onChange={update("company")}
          />
        </Field>
        <ChoiceField
          id={`${fieldId}-team-type`}
          name={`${fieldId}-team-type-option`}
          label="Team type"
          options={TEAM_TYPES}
          value={fields.teamType}
          onChange={(teamType) => setFields((current) => ({ ...current, teamType }))}
        />
      </div>
      <ChoiceField
        id={`${fieldId}-service`}
        name={`${fieldId}-service-option`}
        label="What service are you most interested in?"
        options={SERVICES}
        value={fields.service}
        onChange={(service) => setFields((current) => ({ ...current, service }))}
        wide
      />
      <Field id={`${fieldId}-message`} label="What would you like to promote?">
        {/* Two rows keeps the intrinsic height under the 96px the design draws, so the CSS
            min-height is what sets it; four rows would overshoot it. */}
        <textarea
          id={`${fieldId}-message`}
          name="message"
          rows={2}
          placeholder="A podcast series, a product launch, an upcoming release…"
          maxLength={2000}
          aria-describedby={`${fieldId}-message-hint`}
          value={fields.message}
          onChange={update("message")}
        />
        <p id={`${fieldId}-message-hint`}>Optional — tell us about your content, product or upcoming campaign.</p>
      </Field>
      {isError && (
        <p className="dl-demo-form__error" role="alert">
          That didn’t send. Please try again, or{" "}
          <ExternalLink
            href={`mailto:${siteConfig.contact.sales}`}
            className="underline"
            analyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, "email-link")}
          >
            email us directly
          </ExternalLink>
          .
        </p>
      )}
      <Button
        type="submit"
        variant="marketing"
        size="lg"
        className="dl-cta"
        disabled={isPending}
        {...formAnalytics.submitButtonProps}
      >
        {isPending ? "Sending…" : "Request a Demo"}
      </Button>
    </form>
  );
};
